import m from "mithril";
import Squire from "squire-rte/build/squire";
import i18n from "../i18n/i18n";
import Toasts from "../toasts";
import sanitizeHtml from "sanitize-html";
import ModalManager from "../modal";
import Modal_Hyperlink_Editor from './component_hyperlink_editor';
import TextUtilities from '../utilities/text_utilities';

function sanitizeInput(inputHtml) {
    const cleanHtml = sanitizeHtml(inputHtml, {
        allowedTags: sanitizeHtml.defaults.allowedTags.filter(tag => tag !== "a"),
        allowedAttributes: false,
    });

    return cleanHtml;
}

const TextEditor = {
    // kept for backward compatibility with callers that read TextEditor.content in save functions
    content: null,
    oninit: function (vnode) {
        vnode.state.editor = null;
        vnode.state.content = vnode.attrs.content;
    },
    view: function (vnode) {
        const s = vnode.state;
        const simple = vnode.attrs.simpleToolbar;

        function openHyperlinkEditor() {
            const editor = s.editor;
            if (!editor) return;
            const selection = editor.getSelection();
            const selectedText = editor.getSelectedText();
            let linkUrl = "";
            if (selection.startContainer.nodeType === Node.TEXT_NODE && selectedText !== "") {
                const parentNode = selection.startContainer.parentNode;
                if (parentNode.tagName === "A") linkUrl = parentNode.getAttribute("href");
            }
            ModalManager.open(i18n.t("insert_hyperlink"), [], Modal_Hyperlink_Editor, {
                linkUrl: linkUrl,
                linkText: selectedText,
                callback: function (result) {
                    if (result && result.linkUrl) {
                        const linkHTML = `<a href="${result.linkUrl}" target="_blank">${result.linkText || result.linkUrl}</a>`;
                        editor.insertHTML(linkHTML);
                    }
                }
            });
        }

        return [
            m(".text-editor", { class: simple ? "compact" : "" }, [
                m(".text-editor-toolbar", [
                    m("i.icon-font-bold tooltip", { onclick: function () { s.editor.hasFormat('b') ? s.editor.removeBold() : s.editor.bold(); }, "data-tooltip": i18n.t("bold") }),
                    m("i.icon-font-italic tooltip", { onclick: function () { s.editor.hasFormat('i') ? s.editor.removeItalic() : s.editor.italic(); }, "data-tooltip": i18n.t("italic") }),
                    m("i.icon-underline tooltip", { onclick: function () { s.editor.hasFormat('u') ? s.editor.removeUnderline() : s.editor.underline(); }, "data-tooltip": i18n.t("underline") }),
                    !simple && m("i.icon-undo tooltip", { onclick: function () { s.editor.undo(); }, "data-tooltip": i18n.t("undo") }),
                    !simple && m("i.icon-font-clear-style tooltip", { onclick: function () { s.editor.removeAllFormatting(); }, "data-tooltip": i18n.t("clear_style") }),
                    !simple && m(".tab-dropdown", [
                        m("i.icon-font-size tooltip", {
                            style: "vertical-align: sub;",
                            onclick: function () {
                                document.querySelector(".font-colour-select").classList.remove("open");
                                document.querySelector(".font-size-select").classList.toggle("open");
                            }, "data-tooltip": i18n.t("text_size")
                        }),
                        m("ul.font-size-select", [
                            m("li", { onclick: function () { s.editor.setFontSize("0.3em"); document.querySelector(".font-size-select").classList.remove("open"); } }, "8"),
                            m("li", { onclick: function () { s.editor.setFontSize("0.5em"); document.querySelector(".font-size-select").classList.remove("open"); } }, "12"),
                            m("li", { onclick: function () { s.editor.setFontSize("0.75em"); document.querySelector(".font-size-select").classList.remove("open"); } }, "18"),
                            m("li", { onclick: function () { s.editor.setFontSize("1em"); document.querySelector(".font-size-select").classList.remove("open"); } }, "24"),
                            m("li", { onclick: function () { s.editor.setFontSize("1.3em"); document.querySelector(".font-size-select").classList.remove("open"); } }, "32"),
                            m("li", { onclick: function () { s.editor.setFontSize("2em"); document.querySelector(".font-size-select").classList.remove("open"); } }, "48"),
                            m("li", { onclick: function () { s.editor.setFontSize("2.6em"); document.querySelector(".font-size-select").classList.remove("open"); } }, "64")
                        ])
                    ]),
                    !simple && m(".tab-dropdown", [
                        m("i.icon-font-colour tooltip", {
                            style: "vertical-align: sub;",
                            onclick: function () {
                                document.querySelector(".font-size-select").classList.remove("open");
                                document.querySelector(".font-colour-select").classList.toggle("open");
                            }, "data-tooltip": i18n.t("text_colour")
                        }),
                        m("ul.font-colour-select", [
                            m("li", { onclick: function () { s.editor.setTextColour("#ccc"); document.querySelector(".font-colour-select").classList.remove("open"); }, style: "background: #ccc;" }),
                            m("li", { onclick: function () { s.editor.setTextColour("#888"); document.querySelector(".font-colour-select").classList.remove("open"); }, style: "background: #888;" }),
                            m("li", { onclick: function () { s.editor.setTextColour("#555"); document.querySelector(".font-colour-select").classList.remove("open"); }, style: "background: #555;" }),
                            m("li", { onclick: function () { s.editor.setTextColour("#000"); document.querySelector(".font-colour-select").classList.remove("open"); }, style: "background: #000;" }),
                            m("li", { onclick: function () { s.editor.setTextColour("#f00"); document.querySelector(".font-colour-select").classList.remove("open"); }, style: "background: #f00;" }),
                            m("li", { onclick: function () { s.editor.setTextColour("#0f0"); document.querySelector(".font-colour-select").classList.remove("open"); }, style: "background: #0f0;" }),
                            m("li", { onclick: function () { s.editor.setTextColour("#00f"); document.querySelector(".font-colour-select").classList.remove("open"); }, style: "background: #00f;" }),
                            m("li", { onclick: function () { s.editor.setTextColour("#fff"); document.querySelector(".font-colour-select").classList.remove("open"); }, style: "background: #fff;" })
                        ])
                    ]),
                    !simple && !vnode.attrs.hideHyperlink && m("i.icon-hyperlink tooltip", { onclick: openHyperlinkEditor, "data-tooltip": i18n.t("hyperlink") }),
                    !simple && m("i.icon-align-left tooltip", { onclick: function () { s.editor.setTextAlignment("left"); }, "data-tooltip": i18n.t("align_left") }),
                    !simple && m("i.icon-align-center tooltip", { onclick: function () { s.editor.setTextAlignment("center"); }, "data-tooltip": i18n.t("align_center") }),
                    !simple && m("i.icon-align-right tooltip", { onclick: function () { s.editor.setTextAlignment("right"); }, "data-tooltip": i18n.t("align_right") }),
                    !simple && m("i.icon-list-bullet tooltip", { onclick: function () { s.editor.makeUnorderedList(); }, "data-tooltip": i18n.t("list_bullet") }),
                    !simple && m("i.icon-list-numbered tooltip", { onclick: function () { s.editor.makeOrderedList(); }, "data-tooltip": i18n.t("list_numbered") }),
                    !simple && m("i.icon-list tooltip", { onclick: function () { s.editor.removeList(); }, "data-tooltip": i18n.t("remove_list") }),
                    !simple && m("i.icon-indent-more tooltip", { onclick: function () { s.editor.increaseQuoteLevel(); }, "data-tooltip": i18n.t("indent_more") }),
                    !simple && m("i.icon-indent-less tooltip", { onclick: function () { s.editor.decreaseQuoteLevel(); }, "data-tooltip": i18n.t("indent_less") })
                ]),
                m(".input richtext", {
                    oncreate: function (e) {
                        s.editor = new Squire(e.dom);
                        s.editor.setHTML(TextUtilities.safeDecodeURIComponent(s.content));
                        s.editor.addEventListener("input", function () {
                            s.content = s.editor.getHTML();
                            TextEditor.content = s.content;
                            if (vnode.attrs.onchange) vnode.attrs.onchange(s.content);
                            m.redraw();
                        });
                        s.editor.addEventListener("willPaste", (event) => {
                            let pastedHTML = "";
                            event.fragment.childNodes.forEach((node) => {
                                const tempDiv = document.createElement("div");
                                tempDiv.appendChild(node.cloneNode(true));
                                pastedHTML += tempDiv.innerHTML;
                            });
                            const sanitizedHTML = sanitizeInput(pastedHTML);
                            while (event.fragment.firstChild) {
                                event.fragment.removeChild(event.fragment.firstChild);
                            }
                            const sanitizedFragment = document.createRange().createContextualFragment(sanitizedHTML);
                            event.fragment.appendChild(sanitizedFragment);
                        });
                    }
                })
            ])
        ];
    }
};

export default TextEditor;
